import { Injectable, Provider } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { isNotNullish } from '@examdojo/core/util/nullish';
import isEqual from 'lodash/isEqual';
import {
  McqSelectionService,
  McqAttemptResponseQuery,
  McqAttemptResponseService,
  QuestionAttemptEntityQuery,
} from '@examdojo/question';
import { debounceTime, distinctUntilChanged, EMPTY, filter, merge, Subject, switchMap, tap } from 'rxjs';
import { ErrorHandlerService } from '@examdojo/core/error-handling';

interface SyncRequest {
  questionAttemptId: string;
  selectedOptionIndexes: number[];
}

@Injectable()
export class SyncedMcqSelectionService extends McqSelectionService {
  constructor(
    private readonly questionAttemptQuery: QuestionAttemptEntityQuery,
    private readonly mcqResponseService: McqAttemptResponseService,
    private readonly mcqResponseQuery: McqAttemptResponseQuery,
    private readonly errorHandlerService: ErrorHandlerService,
  ) {
    super();

    merge(this.syncSelectionFromStoreOnQuestionChange(), this.debounceUpsert()).pipe(takeUntilDestroyed()).subscribe();
  }

  private readonly upsertSelectedOptionIndexes$$ = new Subject<SyncRequest>();

  selectMultipleChoiceOption(optionIndex: number) {
    const questionAttemptId = this.questionAttemptQuery.getActiveId();
    if (!questionAttemptId) {
      return;
    }

    this.multipleChoiceOptionSelected.set([optionIndex]);

    this.mcqResponseService.upsertToLocalStore(questionAttemptId, {
      selected_option_indexes: [optionIndex],
    });

    this.upsertSelectedOptionIndexes$$.next({ questionAttemptId, selectedOptionIndexes: [optionIndex] });
  }

  resetMultipleChoiceOptionSelected(onlyClearOptions?: number[]) {
    if (!onlyClearOptions) {
      return this.multipleChoiceOptionSelected.set([]);
    }

    const newValue = this.multipleChoiceOptionSelected().filter((option) => !onlyClearOptions.includes(option));
    this.multipleChoiceOptionSelected.set(newValue);
  }

  private debounceUpsert() {
    return this.upsertSelectedOptionIndexes$$.pipe(
      debounceTime(500),
      switchMap(({ questionAttemptId, selectedOptionIndexes }) =>
        this.mcqResponseService
          .upsertSelectedOptionIndexes(questionAttemptId, selectedOptionIndexes)
          .pipe(
            this.errorHandlerService.catchError(
              `[SyncedMcqSelectionService.debouncedSyncToDb]: failed to sync the multiple choice option to the database`,
              () => EMPTY,
              { toast: `Could not sync the multiple choice option to the database` },
            ),
          ),
      ),
    );
  }

  private syncSelectionFromStoreOnQuestionChange() {
    return this.questionAttemptQuery.selectActiveId().pipe(
      filter(isNotNullish),
      distinctUntilChanged(),
      tap(() => {
        this.resetMultipleChoiceOptionSelected();
      }),
      switchMap((questionAttemptId) => {
        return this.mcqResponseQuery.selectByQuestionAttemptId(questionAttemptId);
      }),
      tap((mcqResponse) => {
        if (mcqResponse && !isEqual(this.multipleChoiceOptionSelected(), mcqResponse.selected_option_indexes)) {
          this.multipleChoiceOptionSelected.set(mcqResponse.selected_option_indexes);
        }
      }),
    );
  }
}

export function provideSyncedMcqSelection(): Provider[] {
  return [SyncedMcqSelectionService, { provide: McqSelectionService, useExisting: SyncedMcqSelectionService }];
}
